import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query'
import {api} from '@/lib/api'
import type {components} from '@/lib/api-schema'

export type Conceptos = components['schemas']['ConceptosRead']
export type Concepto = components['schemas']['ConceptoRead']
export type Restriccion = components['schemas']['RestriccionRead']
export type ConceptoWrite = components['schemas']['ConceptoWrite']
export type RestriccionWrite = components['schemas']['RestriccionWrite']
export type Lectura = components['schemas']['LecturaRead']
export type LecturaWrite = components['schemas']['LecturaWrite']

export function useConceptos(businessId: string | undefined, employeeId: string | null) {
  return useQuery({
    queryKey: ['conceptos', businessId, employeeId],
    queryFn: async () => (await api.get<Conceptos>(`/businesses/${businessId}/employees/${employeeId}/conceptos`)).data,
    enabled: !!businessId && !!employeeId,
  })
}

/** Los radicados vivos, para avisar antes de registrar si el tecleado no casa con ninguno. */
export function useRadicadosVivos(businessId: string | undefined, employeeId: string | null) {
  return useQuery({
    queryKey: ['solicitudes-ips', businessId, 'radicados', employeeId],
    queryFn: async () =>
      (
        await api.get<{items: {radicado: string}[]}>(`/businesses/${businessId}/solicitudes-ips`, {
          params: {employee_id: employeeId, page_size: 100},
        })
      ).data.items.map((solicitud) => solicitud.radicado),
    enabled: !!businessId && !!employeeId,
  })
}

/** El soporte aterriza como evidencia 3.1.4 ordinaria: la matriz de exámenes y la evidencia
 * también cambian. */
function useConceptoMutation<TInput>(
  businessId: string,
  employeeId: string,
  call: (input: TInput) => Promise<Conceptos>
) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: call,
    onSuccess: (data) => {
      queryClient.setQueryData(['conceptos', businessId, employeeId], data)
      queryClient.invalidateQueries({queryKey: ['examenes', businessId, employeeId]})
      queryClient.invalidateQueries({queryKey: ['evidence', businessId]})
      queryClient.invalidateQueries({queryKey: ['examenes-matriz', businessId]})
    },
  })
}

export function useRegistrarConcepto(businessId: string, employeeId: string) {
  return useConceptoMutation<ConceptoWrite>(
    businessId,
    employeeId,
    async (input) =>
      (await api.post<Conceptos>(`/businesses/${businessId}/employees/${employeeId}/conceptos`, input)).data
  )
}

export function useAnularConcepto(businessId: string, employeeId: string) {
  return useConceptoMutation<{examenId: string; motivo: string}>(
    businessId,
    employeeId,
    async ({examenId, motivo}) =>
      (
        await api.post<Conceptos>(`/businesses/${businessId}/employees/${employeeId}/conceptos/${examenId}/anular`, {
          motivo,
        })
      ).data
  )
}

/** Propone el registro entero, nunca lo guarda; sin `modelo` en la respuesta nadie lo leyó. */
export function useLeerConcepto(businessId: string, employeeId: string) {
  return useMutation({
    mutationFn: async (input: LecturaWrite) =>
      (await api.post<Lectura>(`/businesses/${businessId}/employees/${employeeId}/conceptos/leer`, input)).data,
  })
}

export type Revisiones = components['schemas']['RevisionesRead']
export type Revision = components['schemas']['RevisionRead']
export type RevisionHallazgo = components['schemas']['EnfasisHallazgoRead']
export type RevisionCierre = components['schemas']['RevisionCierre']

/** GET que escribe (la revisión que se cierra sola deja registro): se pide una vez por
 * pantalla y se reusa. */
export function useRevisionConcepto(businessId: string | undefined, employeeId: string | null) {
  return useQuery({
    queryKey: ['conceptos', businessId, employeeId, 'revision'],
    queryFn: async () =>
      (await api.get<Revisiones>(`/businesses/${businessId}/employees/${employeeId}/conceptos/revision`)).data,
    enabled: !!businessId && !!employeeId,
  })
}

/** Lo único que alimenta la evaluación de la IPS. */
export function useCerrarRevision(businessId: string, employeeId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (input: {revisionId: string; cierre: RevisionCierre}) =>
      (
        await api.post<Revisiones>(
          `/businesses/${businessId}/employees/${employeeId}/conceptos/revision/${input.revisionId}/cerrar`,
          {cierre: input.cierre}
        )
      ).data,
    onSuccess: (data) => {
      queryClient.setQueryData(['conceptos', businessId, employeeId, 'revision'], data)
    },
  })
}

export type Seguimiento = components['schemas']['SeguimientoRead']
export type RestriccionSeguimiento = components['schemas']['RestriccionSeguimientoRead']
export type AccionDeRestriccion = components['schemas']['AccionDeRestriccionRead']
export type DecidirWrite = components['schemas']['DecidirWrite']
export type AccionWrite = components['schemas']['AccionWrite']

/** GET que escribe: mueve a «vencida» lo que el calendario venció. Se pide una vez por pantalla. */
export function useSeguimientoRestricciones(businessId: string | undefined, employeeId: string | null) {
  return useQuery({
    queryKey: ['conceptos', businessId, employeeId, 'restricciones'],
    queryFn: async () =>
      (await api.get<Seguimiento>(`/businesses/${businessId}/employees/${employeeId}/conceptos/restricciones`)).data,
    enabled: !!businessId && !!employeeId,
  })
}

/** Decidir vuelve a correr el cruce contra el cargo y confirmar la acción agrega una fila al
 * plan de mejoramiento. */
function useSeguimientoMutation<TInput>(
  businessId: string,
  employeeId: string,
  call: (input: TInput) => Promise<Seguimiento>
) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: call,
    onSuccess: (data) => {
      queryClient.setQueryData(['conceptos', businessId, employeeId, 'restricciones'], data)
      queryClient.invalidateQueries({queryKey: ['cumplimiento', businessId]})
      queryClient.invalidateQueries({queryKey: ['actions', businessId]})
    },
  })
}

export function useDecidirRestriccion(businessId: string, employeeId: string) {
  return useSeguimientoMutation<{restriccionId: string; body: DecidirWrite}>(
    businessId,
    employeeId,
    async ({restriccionId, body}) =>
      (
        await api.post<Seguimiento>(
          `/businesses/${businessId}/employees/${employeeId}/conceptos/restricciones/${restriccionId}/decidir`,
          body
        )
      ).data
  )
}

export function useAccionDeRestriccion(businessId: string, employeeId: string) {
  return useSeguimientoMutation<{restriccionId: string; body: AccionWrite}>(
    businessId,
    employeeId,
    async ({restriccionId, body}) =>
      (
        await api.post<Seguimiento>(
          `/businesses/${businessId}/employees/${employeeId}/conceptos/restricciones/${restriccionId}/accion`,
          body
        )
      ).data
  )
}
